import { alpha, Box, Button, Divider, Stack, Typography, Theme } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import React from 'react';
import useBundleTranslation from 'i18n';
import { IVariable } from './index';
import Variable from './Variable';
import { uid } from 'tools/tools';

export default function Variables({
    variables,
    setVariables,
}: {
    variables: IVariable[];
    setVariables: (variables: IVariable[]) => void;
}) {
    const { t } = useBundleTranslation(['components/common/secure_json_editor']);

    const [isExpanded, setIsExpanded] = React.useState(false);

    const createNewVariable = () => {
        const newVariables = [...variables];

        newVariables.unshift({
            uid: uid(),
            name: '',
            value: '',
            encrypted: true,
            isNew: true,
        });

        setVariables(newVariables);
    };

    const updateVariable = (variable: IVariable) => {
        const newVariables = variables.map((item) => {
            if (item.uid === variable.uid) {
                return variable;
            }

            return item;
        });

        setVariables(newVariables);
    };

    const deleteVariable = (variable: IVariable) => {
        const newVariables = variables.filter((item) => item.uid !== variable.uid);

        setVariables(newVariables);
    };

    return (
        <Stack direction={'row'} sx={{ width: isExpanded ? '500px' : '200px', position: 'relative' }}>
            <Stack
                justifyContent={'center'}
                alignItems={'center'}
                sx={{
                    cursor: 'pointer',
                    position: 'absolute',
                    left: '-31px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '32px',
                    height: '112px',
                    minWidth: '32px',
                    padding: 0,
                    border: '1px solid',
                    borderRightWidth: 0,
                    borderTopLeftRadius: '3px',
                    borderBottomLeftRadius: '3px',
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    backgroundColor: 'text.opposite',
                    borderColor: (theme: Theme) => theme.palette.divider,
                    opacity: 1,
                    color: 'primary.main',
                    fontSize: '16px',
                    '&:hover': {
                        backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.04),
                    },
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <IconMi icon={isExpanded ? 'arrow-right' : 'arrow-left'} />
            </Stack>
            <Box
                sx={{
                    width: '100%',
                    p: 2,
                    pt: 4,
                    borderLeft: '1px solid',
                    borderColor: (theme: Theme) => theme.palette.divider,
                }}
            >
                <Box sx={{ width: '100%', margin: '0 auto' }}>
                    <Stack direction={'row'} alignItems={'flex-end'}>
                        <Typography variant="heading" width={'100%'}>
                            {t('variables')}
                        </Typography>
                        {isExpanded && (
                            <Button
                                sx={{ flexShrink: 0, ml: 1.5 }}
                                variant={'light'}
                                color={'success'}
                                startIcon={<IconMi icon={'new'} />}
                                onClick={createNewVariable}
                            >
                                {t('create_variable')}
                            </Button>
                        )}
                    </Stack>
                </Box>
                <Box
                    sx={{
                        position: 'relative',
                        height: '100%',
                    }}
                >
                    {variables.length > 0 && (
                        <Stack
                            direction={'row'}
                            justifyContent={'center'}
                            alignItems={'center'}
                            sx={{
                                pl: 1,
                                pr: 2,
                                overflow: 'auto',
                                borderRadius: '3px',
                                backgroundColor: 'divider',
                                mt: 2,
                                height: 32,
                                color: (theme) => alpha(theme.palette.text.primary, 0.8),
                            }}
                        >
                            {isExpanded ? (
                                <>
                                    <Box width={0.5}>{t('name_label')}</Box>
                                    <Box width={1} paddingLeft={'8px'}>
                                        {t('value_label')}
                                    </Box>
                                    <Box width={0.25} paddingLeft={'8px'}>
                                        {t('encrypted_label')}
                                    </Box>
                                    <Box flexShrink={0} width={'24px'} />
                                </>
                            ) : (
                                <Box width={1}>{t('name_label')}</Box>
                            )}
                        </Stack>
                    )}
                    <Box
                        sx={{
                            pt: 0.5,
                            overflow: 'auto',
                        }}
                    >
                        <Stack spacing={0.75} sx={{ width: '100%', margin: '0 auto' }}>
                            {variables.map((variable, index) => {
                                if (!isExpanded) {
                                    return (
                                        <React.Fragment key={variable.uid}>
                                            <Box
                                                sx={{
                                                    pl: 1,
                                                    pr: 1,
                                                    overflow: 'auto',
                                                }}
                                                width={1}
                                            >
                                                {variable.name}
                                            </Box>
                                            <Divider />
                                        </React.Fragment>
                                    );
                                }
                                return (
                                    <Variable
                                        key={variable.uid}
                                        variable={variable}
                                        updateVariable={updateVariable}
                                        deleteVariable={deleteVariable}
                                    />
                                );
                            })}
                        </Stack>
                    </Box>
                </Box>
            </Box>
        </Stack>
    );
}
